import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiMail, FiPhone, FiInstagram, FiExternalLink } from 'react-icons/fi';
import styles from './Contact.module.css';

const contactInfo = [
  {
    icon: <FiMail />,
    label: 'Email',
    value: 'uredibilge@gmail.com',
    href: 'mailto:uredibilge@gmail.com',
  },
  {
    icon: <FiPhone />,
    label: 'Phone / WhatsApp',
    value: '+1 778 788 8294',
    href: 'tel:+17787888294',
  },
  {
    icon: <FiInstagram />,
    label: 'Instagram',
    value: '@aussierooart',
    href: 'https://www.instagram.com/aussierooart',
  },
  {
    icon: <FiExternalLink />,
    label: 'Book Online',
    value: 'bookanartist.co',
    href: 'https://bookanartist.co/artist/artist-for-hire/13184',
  },
];

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', project: '', message: '' });
  const [sent, setSent] = useState(false);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = e => {
    e.preventDefault();
    // Opens email client with pre-filled content
    const subject = encodeURIComponent(`Project Inquiry: ${form.project || 'Commission'}`);
    const body = encodeURIComponent(
      `Hello Bilge,\n\nMy name is ${form.name}.\n\nProject Type: ${form.project}\n\n${form.message}\n\nBest regards,\n${form.name}\n${form.email}`
    );
    window.location.href = `mailto:uredibilge@gmail.com?subject=${subject}&body=${body}`;
    setSent(true);
  };

  return (
    <section id="contact" className={styles.contact} ref={ref}>
      <div className={styles.container}>
        <div className={styles.left}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9 }}
          >
            <span className={styles.sectionLabel}>
              <span className={styles.labelLine} /> Get in Touch
            </span>
            <h2 className={styles.heading}>
              Let's Create Something <em>Together</em>
            </h2>
            <p className={styles.subtext}>
              Whether you have a project in mind, a wall that needs transforming, or simply want to
              explore possibilities — reach out. Every great piece starts with a single conversation.
            </p>
          </motion.div>

          <motion.div
            className={styles.contactList}
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.2 }}
          >
            {contactInfo.map(item => (
              <a
                key={item.label}
                href={item.href}
                target={item.href.startsWith('http') ? '_blank' : undefined}
                rel="noopener noreferrer"
                className={styles.contactItem}
              >
                <span className={styles.contactIcon}>{item.icon}</span>
                <div>
                  <span className={styles.contactLabel}>{item.label}</span>
                  <span className={styles.contactValue}>{item.value}</span>
                </div>
              </a>
            ))}
          </motion.div>

          <motion.div
            className={styles.locationNote}
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ delay: 0.5 }}
          >
            {/* TODO: Add specific location/studio address if desired */}
            <span className={styles.locationPin}>📍</span>
            <span>Vancouver, British Columbia, Canada</span>
          </motion.div>
        </div>

        {/* Form */}
        <motion.div
          className={styles.right}
          initial={{ opacity: 0, x: 40 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.9, delay: 0.15 }}
        >
          {sent ? (
            <div className={styles.successMsg}>
              <span className={styles.successIcon}>✓</span>
              <h3>Your email client is open!</h3>
              <p>Fill in the details and send — I'll get back to you as soon as possible.</p>
              <button className={styles.resetBtn} onClick={() => setSent(false)}>Send Another</button>
            </div>
          ) : (
            <form className={styles.form} onSubmit={handleSubmit}>
              <h3 className={styles.formTitle}>Send a Message</h3>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="name">Your Name</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    placeholder="Jane Smith"
                    value={form.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className={styles.formGroup}>
                  <label htmlFor="email">Email Address</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="jane@example.com"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="project">Project Type</label>
                <select
                  id="project"
                  name="project"
                  value={form.project}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a service...</option>
                  <option value="Custom Mural">Custom Mural</option>
                  <option value="Canvas & Studio Art">Canvas & Studio Art</option>
                  <option value="Surface & Textile Design">Surface & Textile Design</option>
                  <option value="Commissioned Artwork">Commissioned Artwork</option>
                  <option value="Other">Other / Not Sure Yet</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label htmlFor="message">Your Message</label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  placeholder="Tell me about your project, space, vision, or any questions you have..."
                  value={form.message}
                  onChange={handleChange}
                  required
                />
              </div>
              <button type="submit" className={styles.submitBtn}>
                Send Message →
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
